import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Plus, Edit, Trash2, CalendarDays, Clock, Settings, Lock, Unlock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/errorUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';
import MainLayout from '../components/MainLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Stage 73.20ed — India FY (April→March) derivation, matches backend
// `_derive_fy_ay` in routes_payroll.py so the create/edit modal can show a
// live preview before the row is even saved.
const deriveFyAy = (year, month) => {
  const y = Number(year), m = Number(month);
  if (!y || !m || m < 1 || m > 12) return { fy: '', ay: '' };
  const fyStart = m >= 4 ? y : y - 1;
  return {
    fy: `${fyStart}-${String(fyStart + 1).slice(-2)}`,
    ay: `${fyStart + 1}-${String(fyStart + 2).slice(-2)}`,
  };
};

// Extract (year, month) from an ISO date `YYYY-MM-DD` or a `YYYY-MM` string.
const ymFromString = (s) => {
  if (!s) return { year: null, month: null };
  const parts = String(s).split('-');
  if (parts.length < 2) return { year: null, month: null };
  return { year: Number(parts[0]), month: Number(parts[1]) };
};

const PayrollSetup = () => {
  const { user } = useAuth();
  const [calendars, setCalendars] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCalModal, setShowCalModal] = useState(false);
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [activeTab, setActiveTab] = useState('calendars');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [calForm, setCalForm] = useState({
    name: '', code: '', pay_cycle: 'monthly', pay_day: 1,
    attendance_cutoff_day: 25, processing_start_day: 26, period_start_day: 1,
    // Advanced fields (split-period boundary + statutory month rule).
    // Optional — only required if Period Boundary is set to non-default.
    period_boundary: '1_to_last',
    statutory_month_rule: 'payment_month',
    custom_start_day: 1, custom_end_day: 31,
  });
  const [calMode, setCalMode] = useState('simple');  // 'simple' | 'advanced'
  const [editCal, setEditCal] = useState(null);
  const [periodForm, setPeriodForm] = useState({ calendar_id: '', period_name: '', period_type: 'regular', start_date: '', end_date: '', pay_date: '' });
  const [editPeriod, setEditPeriod] = useState(null);
  const [statConfigs, setStatConfigs] = useState({});
  const [showStatModal, setShowStatModal] = useState(false);
  const [statType, setStatType] = useState('pf');
  const [statForm, setStatForm] = useState({});
  const [periodYearMonth, setPeriodYearMonth] = useState(''); // YYYY-MM picker for auto-fill
  // Stage 73.20cs — Base Days source config (per CTC group).
  // Rows: [{ctc_group_id, code, name, parent_group, base_days_source, ...}]
  const [baseDaysRows, setBaseDaysRows] = useState([]);
  const [baseDaysDirty, setBaseDaysDirty] = useState({});
  const [baseDaysSaving, setBaseDaysSaving] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  const fetchCalendars = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/payroll/calendars?company_id=${selectedCompany}`, { withCredentials: true });
      setCalendars(data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [selectedCompany]);

  const fetchPeriods = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/payroll/periods?company_id=${selectedCompany}`, { withCredentials: true });
      setPeriods(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  const fetchStatutory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/payroll/statutory?company_id=${selectedCompany}`, { withCredentials: true });
      setStatConfigs(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  // Stage 73.20cs — Load per-CTC-group Base Days source config.
  const fetchBaseDaysConfig = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(
        `${API_URL}/controls/base-days-source-config?company_id=${selectedCompany}`,
        { withCredentials: true },
      );
      setBaseDaysRows(Array.isArray(data) ? data : []);
      setBaseDaysDirty({});
    } catch (e) {
      setBaseDaysRows([]);
    }
  }, [selectedCompany]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    if (selectedCompany) { fetchCalendars(); fetchPeriods(); fetchStatutory(); fetchBaseDaysConfig(); }
  }, [selectedCompany, fetchCalendars, fetchPeriods, fetchStatutory, fetchBaseDaysConfig]);

  const setRowSource = (gid, source) => {
    setBaseDaysDirty(prev => ({ ...prev, [gid]: source }));
  };

  const saveBaseDaysConfig = async () => {
    const rows = Object.entries(baseDaysDirty).map(([ctc_group_id, base_days_source]) => ({
      ctc_group_id, base_days_source,
    }));
    if (rows.length === 0) { toast.info('No changes to save'); return; }
    setBaseDaysSaving(true);
    try {
      await axios.put(
        `${API_URL}/controls/base-days-source-config`,
        { company_id: selectedCompany, rows },
        { withCredentials: true },
      );
      toast.success(`Updated Base Days source for ${rows.length} group(s)`);
      fetchBaseDaysConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setBaseDaysSaving(false);
    }
  };

  const handleCreateCalendar = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/payroll/calendars`, { ...calForm, company_id: selectedCompany }, { withCredentials: true });
      setShowCalModal(false);
      setCalForm({ name: '', pay_cycle: 'monthly', pay_day: 1, attendance_cutoff_day: 25, processing_start_day: 26, period_start_day: 1 });
      fetchCalendars();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleCreatePeriod = async (e) => {
    e.preventDefault();
    try {
      // Stage 73.20ed — Derive statutory year/month from the YM picker if used,
      // otherwise from start_date. Backend uses these to stamp FY/AY on the doc.
      const ym = periodYearMonth
        ? ymFromString(periodYearMonth)
        : ymFromString(periodForm.start_date);
      const payload = {
        ...periodForm,
        company_id: selectedCompany,
        ...(ym.year && ym.month ? { statutory_year: ym.year, statutory_month: ym.month } : {}),
      };
      await axios.post(`${API_URL}/payroll/periods`, payload, { withCredentials: true });
      setShowPeriodModal(false);
      setPeriodForm({ calendar_id: '', period_name: '', period_type: 'regular', start_date: '', end_date: '', pay_date: '' });
      setPeriodYearMonth('');
      fetchPeriods();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handlePeriodAction = async (periodId, action) => {
    try {
      const newStatus = action === 'lock' ? 'locked' : 'open';
      await axios.patch(`${API_URL}/payroll/periods/${periodId}`, { status: newStatus, remarks: `${action} by admin` }, { withCredentials: true });
      toast.success(`Period ${action === 'lock' ? 'locked' : 'reopened'} successfully`);
      fetchPeriods();
    } catch (err) { toast.error(err.response?.data?.detail || `Failed to ${action} period`); }
  };

  const handleSaveStatutory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/payroll/statutory/${statType}`, { ...statForm, company_id: selectedCompany }, { withCredentials: true });
      toast.success(`${statType.toUpperCase()} configuration saved`);
      setShowStatModal(false);
      fetchStatutory();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ====== Helpers for calendar ↔ period linking clarity ======================
  // Fast lookup from `calendar_id` → calendar object so the Periods table can
  // show which calendar each period belongs to (the missing link the user
  // complained about). Some legacy periods use `calendar_config_id` instead —
  // we accept both so older data doesn't show up as "orphan".
  const calendarById = React.useMemo(() => {
    const m = {};
    for (const c of calendars) m[c.id] = c;
    return m;
  }, [calendars]);

  const getPeriodCalendarId = (p) => p.calendar_id || p.calendar_config_id || '';

  // Period count per calendar — drives the "📅 5 periods" chip on the Calendars
  // table. O(N) one-time aggregation.
  const periodCountByCalendar = React.useMemo(() => {
    const m = {};
    for (const p of periods) {
      const cid = getPeriodCalendarId(p);
      if (cid) m[cid] = (m[cid] || 0) + 1;
    }
    return m;
  }, [periods]);

  // Auto-fill start/end/pay dates from the selected calendar + a year-month.
  // Fires whenever the user changes either dropdown in the Period modal.
  const autoFillPeriodFromCalendar = async (calendarId, yearMonth) => {
    if (!calendarId || !yearMonth) return;
    try {
      const { data } = await axios.get(
        `${API_URL}/payroll/calendars/${calendarId}/period-suggest?year_month=${yearMonth}`,
        { withCredentials: true },
      );
      setPeriodForm((prev) => ({
        ...prev,
        calendar_id: calendarId,
        period_name: prev.period_name || data.period_name,
        start_date: data.start_date,
        end_date: data.end_date,
        pay_date: data.pay_date,
      }));
    } catch (err) {
      // Non-blocking — the user can still type dates manually.
      console.warn('period-suggest failed', err);
    }
  };

  // ====== Calendar edit / delete ============================================
  // Both were referenced by onClick handlers but never declared — caused
  // "openEditCalendar is not defined" runtime errors when the user hit Edit.
  const openEditCalendar = (cal) => {
    setEditCal(cal);
    setCalForm({
      name: cal.name || '',
      code: cal.code || '',
      pay_cycle: cal.pay_cycle || 'monthly',
      pay_day: cal.pay_day ?? 1,
      attendance_cutoff_day: cal.attendance_cutoff_day ?? 25,
      processing_start_day: cal.processing_start_day ?? 26,
      period_start_day: cal.period_start_day ?? 1,
      // Advanced fields — pre-populated so editing an "advanced" calendar
      // keeps the split-period config visible in the form.
      period_boundary: cal.period_boundary || '1_to_last',
      statutory_month_rule: cal.statutory_month_rule || 'payment_month',
      custom_start_day: cal.custom_start_day ?? 1,
      custom_end_day: cal.custom_end_day ?? 31,
    });
    // Auto-open in Advanced view if this calendar uses any advanced setting.
    setCalMode((cal.period_boundary && cal.period_boundary !== '1_to_last')
      || cal.code ? 'advanced' : 'simple');
    setShowCalModal(true);
  };

  // Generate 12 periods for a year from a calendar — used by the Advanced view
  // and matches the existing Business-Controls behaviour (one click → 12 months).
  const handleGeneratePeriods = async (calId, calName) => {
    const year = parseInt(window.prompt(
      `Generate 12 monthly periods for "${calName}". Which year?`,
      String(new Date().getFullYear()),
    ));
    if (!year || year < 2020 || year > 2100) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/controls/calendar-config/${calId}/generate-periods?year=${year}`,
        null, { withCredentials: true },
      );
      toast.success(`Generated ${data?.created || 12} periods for ${year}`);
      fetchPeriods();
    } catch (err) {
      toast.error(formatApiError(err) || 'Could not generate periods');
    }
  };

  const handleUpdateCalendar = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(
        `${API_URL}/payroll/calendars/${editCal.id}`,
        { ...calForm, company_id: selectedCompany },
        { withCredentials: true },
      );
      toast.success('Calendar updated');
      setShowCalModal(false);
      setEditCal(null);
      fetchCalendars();
    } catch (err) {
      toast.error(formatApiError(err) || 'Could not update calendar');
    }
  };

  const handleDeleteCalendar = async (calId, calName) => {
    if (!window.confirm(`Delete calendar "${calName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/payroll/calendars/${calId}`, { withCredentials: true });
      toast.success('Calendar deleted');
      fetchCalendars();
    } catch (err) {
      toast.error(formatApiError(err) || 'Could not delete calendar');
    }
  };

  // ====== Period edit / delete ==============================================
  const openEditPeriod = (p) => {
    setEditPeriod(p);
    setPeriodForm({
      // Legacy data uses `calendar_config_id`; new data uses `calendar_id`.
      calendar_id: p.calendar_id || p.calendar_config_id || '',
      period_name: p.period_name || '',
      period_type: p.period_type || 'regular',
      start_date: p.start_date || '',
      end_date: p.end_date || '',
      pay_date: p.pay_date || '',
    });
    setShowPeriodModal(true);
  };

  const handleUpdatePeriod = async (e) => {
    e.preventDefault();
    try {
      // Stage 73.20ed — On edit, re-derive FY/AY from the (possibly changed)
      // start_date so the doc stays consistent.
      const ym = ymFromString(periodForm.start_date);
      const payload = {
        ...periodForm,
        company_id: selectedCompany,
        ...(ym.year && ym.month ? { statutory_year: ym.year, statutory_month: ym.month } : {}),
      };
      await axios.patch(
        `${API_URL}/payroll/periods/${editPeriod.id}`,
        payload,
        { withCredentials: true },
      );
      toast.success('Period updated');
      setShowPeriodModal(false);
      setEditPeriod(null);
      fetchPeriods();
    } catch (err) {
      toast.error(formatApiError(err) || 'Could not update period');
    }
  };

  const handleDeletePeriod = async (periodId, periodName) => {
    if (!window.confirm(`Delete period "${periodName}"? This will fail if any payroll runs reference it.`)) return;
    try {
      await axios.delete(`${API_URL}/payroll/periods/${periodId}`, { withCredentials: true });
      toast.success('Period deleted');
      fetchPeriods();
    } catch (err) {
      toast.error(formatApiError(err) || 'Could not delete period — likely has linked payroll runs');
    }
  };

  const statusColors = {
    open: 'bg-emerald-100 text-emerald-700', processing: 'bg-blue-100 text-blue-700',
    preview: 'bg-amber-100 text-amber-700', frozen: 'bg-cyan-100 text-cyan-700',
    pending_approval: 'bg-orange-100 text-orange-700', approved: 'bg-green-100 text-green-700',
    released: 'bg-indigo-100 text-indigo-700', locked: 'bg-slate-200 text-slate-700',
  };

  return (
    <MainLayout>
      <div data-testid="payroll-setup-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Payroll Setup</h1>
            <p className="text-sm text-slate-500 mt-1">Configure payroll calendars, periods, and statutory settings</p>
          </div>
          <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            {companies.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
        </div>

        <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
          {['calendars', 'periods', 'statutory', 'base_days'].map((tab) => (
            <button key={tab} data-testid={`tab-${tab}`} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{tab === 'base_days' ? 'Base Days' : tab.charAt(0).toUpperCase() + tab.slice(1)}</button>
          ))}
        </div>

        {activeTab === 'calendars' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="text-base font-semibold text-slate-800">Payroll Calendars</h2>
              <button data-testid="add-calendar-btn" onClick={() => { setEditCal(null); setCalForm({ name: '', pay_cycle: 'monthly', pay_day: 1, attendance_cutoff_day: 25, processing_start_day: 26, period_start_day: 1 }); setShowCalModal(true); }} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC] transition-colors">
                <Plus className="w-4 h-4" /><span>Add Calendar</span>
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600"><tr>
                  <th className="px-4 py-3 text-left font-medium">Name</th>
                  <th className="px-4 py-3 text-left font-medium">Pay Cycle</th>
                  <th className="px-4 py-3 text-left font-medium">Pay Day</th>
                  <th className="px-4 py-3 text-left font-medium">Cutoff Day</th>
                  <th className="px-4 py-3 text-left font-medium">Periods</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {calendars.map((c) => (
                    <tr key={c.id} data-testid={`calendar-row-${c.id}`} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{c.name}</td>
                      <td className="px-4 py-3 text-slate-600 capitalize">{c.pay_cycle}</td>
                      <td className="px-4 py-3 text-slate-600">{c.pay_day}</td>
                      <td className="px-4 py-3 text-slate-600">{c.attendance_cutoff_day}</td>
                      <td className="px-4 py-3">
                        {/* Period-count chip — clicking jumps to the Periods tab
                            with this calendar's name visible at the top. */}
                        <button onClick={() => setActiveTab('periods')}
                          data-testid={`calendar-period-count-${c.id}`}
                          className={`px-2 py-1 rounded-full text-xs font-medium hover:underline ${
                            (periodCountByCalendar[c.id] || 0) > 0
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                          📅 {periodCountByCalendar[c.id] || 0} period{(periodCountByCalendar[c.id] || 0) === 1 ? '' : 's'}
                        </button>
                      </td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{c.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <button data-testid={`edit-calendar-${c.id}`} onClick={() => openEditCalendar(c)} title="Edit calendar"
                            className="p-1.5 text-slate-400 hover:text-[#0052FF] hover:bg-blue-50 rounded">
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`generate-periods-${c.id}`} onClick={() => handleGeneratePeriods(c.id, c.name)} title="Generate 12 monthly periods for a year"
                            className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded">
                            <CalendarDays className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`delete-calendar-${c.id}`} onClick={() => handleDeleteCalendar(c.id, c.name)} title="Delete calendar"
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {calendars.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No calendars configured</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'periods' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="text-base font-semibold text-slate-800">Payroll Periods</h2>
              <button data-testid="add-period-btn" onClick={() => { setEditPeriod(null); setPeriodYearMonth(''); setPeriodForm({ calendar_id: '', period_name: '', period_type: 'regular', start_date: '', end_date: '', pay_date: '' }); setShowPeriodModal(true); }} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC] transition-colors">
                <Plus className="w-4 h-4" /><span>Add Period</span>
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600"><tr>
                  <th className="px-4 py-3 text-left font-medium">Period</th>
                  <th className="px-4 py-3 text-left font-medium">Calendar</th>
                  <th className="px-4 py-3 text-left font-medium">Type</th>
                  <th className="px-4 py-3 text-left font-medium">Start</th>
                  <th className="px-4 py-3 text-left font-medium">End</th>
                  <th className="px-4 py-3 text-left font-medium">Pay Date</th>
                  <th className="px-4 py-3 text-left font-medium">FY / AY</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {periods.map((p) => (
                    <tr key={p.id} data-testid={`period-row-${p.id}`} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{p.period_name}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {/* Show the parent calendar — the "link clarity" the
                            user wanted. Orphan periods (calendar deleted)
                            flag in red. */}
                        {calendarById[getPeriodCalendarId(p)]?.name
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-violet-50 text-violet-800 text-xs">
                              📅 {calendarById[getPeriodCalendarId(p)].name}
                            </span>
                          : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-50 text-red-700 text-xs"
                                  title="This period has no linked calendar — edit it to fix.">
                              ⚠ Orphan
                            </span>
                        }
                      </td>
                      <td className="px-4 py-3 text-slate-600 capitalize">{p.period_type}</td>
                      <td className="px-4 py-3 text-slate-600">{p.start_date}</td>
                      <td className="px-4 py-3 text-slate-600">{p.end_date}</td>
                      <td className="px-4 py-3 text-slate-600">{p.pay_date}</td>
                      <td className="px-4 py-3">
                        {/* Stage 73.20ed — FY / AY column. Prefer server-persisted
                            values (post iteration-81); fall back to the client
                            derivation for periods that pre-date the lazy backfill. */}
                        {(() => {
                          const fyStored = p.financial_year;
                          const ayStored = p.assessment_year;
                          if (fyStored && ayStored) {
                            return (
                              <div className="flex flex-col text-xs" data-testid={`period-fy-ay-${p.id}`}>
                                <span className="font-medium text-slate-700">FY {fyStored}</span>
                                <span className="text-slate-500">AY {ayStored}</span>
                              </div>
                            );
                          }
                          const ym = ymFromString(p.start_date);
                          const { fy, ay } = deriveFyAy(ym.year, ym.month);
                          if (!fy) return <span className="text-slate-400 text-xs">—</span>;
                          return (
                            <div className="flex flex-col text-xs" data-testid={`period-fy-ay-${p.id}`}>
                              <span className="font-medium text-slate-500 italic" title="Derived from start_date (not persisted yet)">FY {fy}</span>
                              <span className="text-slate-400 italic">AY {ay}</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[p.status] || 'bg-slate-100 text-slate-500'}`}>{p.status?.replace('_', ' ')}</span></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          {p.status !== 'locked' && (
                            <button data-testid={`edit-period-${p.id}`} onClick={() => openEditPeriod(p)} title="Edit period"
                              className="p-1.5 text-slate-400 hover:text-[#0052FF] hover:bg-blue-50 rounded">
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {p.status !== 'locked' && (
                            <button data-testid={`lock-period-${p.id}`} onClick={() => handlePeriodAction(p.id, 'lock')}
                              className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded text-xs font-medium flex items-center gap-1" title="Lock Period">
                              <Lock className="w-3 h-3" /> Lock
                            </button>
                          )}
                          {p.status === 'locked' && (
                            <button data-testid={`reopen-period-${p.id}`} onClick={() => handlePeriodAction(p.id, 'reopen')}
                              className="px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded text-xs font-medium flex items-center gap-1" title="Reopen Period">
                              <Unlock className="w-3 h-3" /> Reopen
                            </button>
                          )}
                          <button data-testid={`delete-period-${p.id}`} onClick={() => handleDeletePeriod(p.id, p.period_name)} title="Delete period (only if no runs exist)"
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {periods.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No periods configured</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'statutory' && (
          <div className="space-y-4">
            {['pf', 'esi', 'pt', 'lwf'].map((t) => (
              <div key={t} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 uppercase">{t === 'pf' ? 'Provident Fund (PF)' : t === 'esi' ? 'ESI' : t === 'pt' ? 'Professional Tax' : 'Labour Welfare Fund'}</h3>
                    <p className="text-xs text-slate-500 mt-1">{statConfigs[t] ? 'Configured' : 'Not configured'}</p>
                  </div>
                  <button data-testid={`configure-${t}-btn`} onClick={() => { setStatType(t); setStatForm(statConfigs[t] || {}); setShowStatModal(true); }}
                    className="text-sm text-[#0052FF] hover:underline font-medium">Configure</button>
                </div>
                {statConfigs[t] && (
                  <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                    {t === 'pf' && <><div className="bg-slate-50 p-2 rounded"><p className="text-slate-500">Employee Rate</p><p className="font-semibold">{statConfigs.pf.employee_rate}%</p></div>
                      <div className="bg-slate-50 p-2 rounded"><p className="text-slate-500">Employer Rate</p><p className="font-semibold">{statConfigs.pf.employer_rate}%</p></div>
                      <div className="bg-slate-50 p-2 rounded"><p className="text-slate-500">Wage Ceiling</p><p className="font-semibold">{statConfigs.pf.wage_ceiling?.toLocaleString()}</p></div></>}
                    {t === 'esi' && <><div className="bg-slate-50 p-2 rounded"><p className="text-slate-500">Employee Rate</p><p className="font-semibold">{statConfigs.esi.employee_rate}%</p></div>
                      <div className="bg-slate-50 p-2 rounded"><p className="text-slate-500">Employer Rate</p><p className="font-semibold">{statConfigs.esi.employer_rate}%</p></div>
                      <div className="bg-slate-50 p-2 rounded"><p className="text-slate-500">Wage Ceiling</p><p className="font-semibold">{statConfigs.esi.wage_ceiling?.toLocaleString()}</p></div></>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Stage 73.20cs — Base Days source configuration per CTC group.
            Admins pick whether a group's base_days comes from the Wage Master
            (auto — default for Staff / fixed groups) or Monthly Input (manual
            — for O&M / variable groups). Payroll engine reads this at run time
            and, for 'monthly_input' groups, expects a base_days_override on the
            monthly_inputs row for each employee that month. */}
        {activeTab === 'base_days' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="base-days-tab">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Base Days Source (per CTC Group)</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  <span className="font-medium text-slate-700">Wage Master</span> — engine picks base days automatically from Wage Master / group defaults (Staff, fixed groups).
                  <span className="mx-2 text-slate-400">·</span>
                  <span className="font-medium text-slate-700">Monthly Input</span> — HR enters base days each month on the Monthly Input grid (O&M, variable groups).
                </p>
              </div>
              <button
                data-testid="save-base-days-config-btn"
                onClick={saveBaseDaysConfig}
                disabled={baseDaysSaving || Object.keys(baseDaysDirty).length === 0}
                className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {baseDaysSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                <span>Save {Object.keys(baseDaysDirty).length > 0 ? `(${Object.keys(baseDaysDirty).length})` : ''}</span>
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Code</th>
                    <th className="px-4 py-3 text-left font-medium">Group Name</th>
                    <th className="px-4 py-3 text-left font-medium">Parent</th>
                    <th className="px-4 py-3 text-center font-medium">Employees</th>
                    <th className="px-4 py-3 text-center font-medium">Base Days Source</th>
                  </tr>
                </thead>
                <tbody>
                  {baseDaysRows.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-slate-500 py-8 text-sm">No CTC groups configured for this company. Create one from Controls → CTC Groups first.</td></tr>
                  )}
                  {baseDaysRows.map((g) => {
                    const value = baseDaysDirty[g.ctc_group_id] ?? g.base_days_source;
                    const dirty = baseDaysDirty[g.ctc_group_id] !== undefined;
                    return (
                      <tr key={g.ctc_group_id} data-testid={`base-days-row-${g.code}`} className={`border-t border-slate-100 ${dirty ? 'bg-indigo-50/40' : ''}`}>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700">{g.code}</td>
                        <td className="px-4 py-3 text-slate-900 font-medium">{g.name}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{g.parent_group || '—'}</td>
                        <td className="px-4 py-3 text-center text-slate-600">{g.employee_count || 0}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
                            <button
                              type="button"
                              data-testid={`base-days-src-wage-${g.code}`}
                              onClick={() => setRowSource(g.ctc_group_id, 'wage_master')}
                              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${value === 'wage_master' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >Wage Master (Auto)</button>
                            <button
                              type="button"
                              data-testid={`base-days-src-manual-${g.code}`}
                              onClick={() => setRowSource(g.ctc_group_id, 'monthly_input')}
                              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${value === 'monthly_input' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >Monthly Input (Manual)</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {showCalModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
               onClick={() => { setShowCalModal(false); setEditCal(null); }}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{editCal ? 'Edit Payroll Calendar' : 'New Payroll Calendar'}</h3>

              {/* ===== SIMPLE / ADVANCED TOGGLE ===========================
                  Simple = pay_day + cutoff_day + period_start_day
                  Advanced = adds period_boundary, statutory_month_rule, code,
                             generate-12-periods button. Same underlying record. */}
              <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 mb-4" data-testid="cal-mode-toggle">
                <button type="button" data-testid="cal-mode-simple"
                  onClick={() => setCalMode('simple')}
                  className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-colors ${calMode === 'simple' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
                  Simple
                </button>
                <button type="button" data-testid="cal-mode-advanced"
                  onClick={() => setCalMode('advanced')}
                  className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-colors ${calMode === 'advanced' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
                  Advanced
                </button>
              </div>

              <form onSubmit={editCal ? handleUpdateCalendar : handleCreateCalendar} className="space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Calendar Name</label>
                  <input data-testid="cal-name-input" value={calForm.name} onChange={(e) => setCalForm({ ...calForm, name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>

                {calMode === 'advanced' && (
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Code <span className="text-slate-400 text-xs">(short identifier for API + reports)</span></label>
                    <input data-testid="cal-code-input" value={calForm.code}
                      onChange={(e) => setCalForm({ ...calForm, code: e.target.value.toUpperCase() })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                      placeholder="STD_CAL" />
                  </div>
                )}

                <div><label className="block text-sm font-medium text-slate-700 mb-1">Pay Cycle</label>
                  <select data-testid="cal-cycle-select" value={calForm.pay_cycle} onChange={(e) => setCalForm({ ...calForm, pay_cycle: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    <option value="monthly">Monthly</option><option value="weekly">Weekly</option><option value="bi_weekly">Bi-Weekly</option></select></div>

                {calMode === 'advanced' && (
                  <>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Period Boundary</label>
                      <select data-testid="cal-boundary-select" value={calForm.period_boundary}
                        onChange={(e) => {
                          const v = e.target.value;
                          // Auto-sync period_start_day to keep the simple view consistent.
                          const psd = v === '21_to_20' ? 21 : v === '26_to_25' ? 26 : v === '16_to_15' ? 16 : 1;
                          setCalForm({ ...calForm, period_boundary: v, period_start_day: psd });
                        }}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                        <option value="1_to_last">1st to Last (calendar month)</option>
                        <option value="21_to_20">21st to 20th</option>
                        <option value="26_to_25">26th to 25th</option>
                        <option value="16_to_15">16th to 15th</option>
                        <option value="custom">Custom (set days below)</option>
                      </select>
                      <p className="text-[10px] text-slate-400 mt-1">Split-period boundary for attendance windows</p>
                    </div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Statutory Month Rule</label>
                      <select data-testid="cal-stat-rule-select" value={calForm.statutory_month_rule}
                        onChange={(e) => setCalForm({ ...calForm, statutory_month_rule: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                        <option value="payment_month">Payment Month (Apr salary in Apr)</option>
                        <option value="working_month">Working Month (Mar work in Mar)</option>
                      </select>
                    </div>
                    {calForm.period_boundary === 'custom' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className="block text-xs font-medium text-slate-700 mb-1">Custom Start Day</label>
                          <input type="number" min={1} max={31} value={calForm.custom_start_day}
                            onChange={(e) => setCalForm({ ...calForm, custom_start_day: parseInt(e.target.value) || 1, period_start_day: parseInt(e.target.value) || 1 })}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                        </div>
                        <div><label className="block text-xs font-medium text-slate-700 mb-1">Custom End Day</label>
                          <input type="number" min={1} max={31} value={calForm.custom_end_day}
                            onChange={(e) => setCalForm({ ...calForm, custom_end_day: parseInt(e.target.value) || 31 })}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="grid grid-cols-2 gap-3">
                  {calMode === 'simple' && (
                    <div><label className="block text-xs font-medium text-slate-700 mb-1">Period Start Day</label>
                      <select data-testid="cal-period-start" value={calForm.period_start_day} onChange={(e) => setCalForm({ ...calForm, period_start_day: parseInt(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                        <option value={1}>1st (1st to month end)</option>
                        <option value={21}>21st (21st to 20th)</option>
                        <option value={26}>26th (26th to 25th)</option>
                      </select>
                    </div>
                  )}
                  <div className={calMode === 'simple' ? '' : 'col-span-2'}><label className="block text-xs font-medium text-slate-700 mb-1">Pay Day</label><input type="number" min={1} max={31} value={calForm.pay_day} onChange={(e) => setCalForm({ ...calForm, pay_day: parseInt(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Attendance Cutoff Day</label><input type="number" min={1} max={31} value={calForm.attendance_cutoff_day} onChange={(e) => setCalForm({ ...calForm, attendance_cutoff_day: parseInt(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Processing Start Day</label><input type="number" min={1} max={31} value={calForm.processing_start_day} onChange={(e) => setCalForm({ ...calForm, processing_start_day: parseInt(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowCalModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="save-calendar-btn" type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC]">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Period Modal */}
        {showPeriodModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
               onClick={() => { setShowPeriodModal(false); setEditPeriod(null); }}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">{editPeriod ? 'Edit Payroll Period' : 'New Payroll Period'}</h3>
              <form onSubmit={editPeriod ? handleUpdatePeriod : handleCreatePeriod} className="space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Calendar <span className="text-red-500">*</span></label>
                  <select data-testid="period-cal-select"
                    value={periodForm.calendar_id}
                    onChange={(e) => {
                      const cid = e.target.value;
                      setPeriodForm({ ...periodForm, calendar_id: cid });
                      // If a month is already chosen, auto-recompute dates.
                      if (cid && periodYearMonth) {
                        autoFillPeriodFromCalendar(cid, periodYearMonth);
                      }
                    }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required>
                    <option value="">— Select Calendar —</option>
                    {calendars.map((c) => (
                      <option key={c.id} value={c.id}>{`${c.name} (${c.pay_cycle} · pay day ${c.pay_day} · cutoff ${c.attendance_cutoff_day})`}</option>
                    ))}
                  </select>
                  {periodForm.calendar_id && calendarById[periodForm.calendar_id] && (
                    <p className="text-[10px] text-violet-700 mt-1">
                      Linked to <b>{calendarById[periodForm.calendar_id].name}</b> ·
                      Period starts day {calendarById[periodForm.calendar_id].period_start_day || 1} ·
                      Pay day {calendarById[periodForm.calendar_id].pay_day}
                    </p>
                  )}
                </div>

                {/* === MONTH PICKER → auto-fills start/end/pay dates ===
                    The user picks the wage month and we ask the backend to
                    compute the start/end/pay dates that match the calendar's
                    settings. No more "what date should pay be?" guesswork. */}
                {!editPeriod && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <label className="block text-xs font-semibold text-blue-900 mb-1">
                      🪄 Wage Month — auto-fills dates below
                    </label>
                    <input data-testid="period-month-picker" type="month"
                      value={periodYearMonth}
                      onChange={(e) => {
                        const ym = e.target.value;
                        setPeriodYearMonth(ym);
                        if (periodForm.calendar_id && ym) {
                          autoFillPeriodFromCalendar(periodForm.calendar_id, ym);
                        }
                      }}
                      disabled={!periodForm.calendar_id}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400" />
                    <p className="text-[10px] text-blue-700 mt-1">
                      {periodForm.calendar_id
                        ? 'Pick the wage month — we\'ll compute period start, end and pay date based on the calendar.'
                        : 'Select a calendar first to enable this shortcut.'}
                    </p>
                  </div>
                )}

                <div><label className="block text-sm font-medium text-slate-700 mb-1">Period Name</label>
                  <input data-testid="period-name-input" value={periodForm.period_name} onChange={(e) => setPeriodForm({ ...periodForm, period_name: e.target.value })} placeholder="e.g. February 2026" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select value={periodForm.period_type} onChange={(e) => setPeriodForm({ ...periodForm, period_type: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    <option value="regular">Regular</option><option value="off_cycle">Off-Cycle</option><option value="arrear">Arrear</option><option value="bonus">Bonus</option></select></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Start Date</label><input type="date" data-testid="period-start-input" value={periodForm.start_date} onChange={(e) => setPeriodForm({ ...periodForm, start_date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">End Date</label><input type="date" data-testid="period-end-input" value={periodForm.end_date} onChange={(e) => setPeriodForm({ ...periodForm, end_date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Pay Date</label><input type="date" value={periodForm.pay_date} onChange={(e) => setPeriodForm({ ...periodForm, pay_date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                </div>
                {/* Stage 73.20ed — Live FY / AY preview. Derived from the wage-
                    month picker (preferred) or from start_date (fallback), so the
                    admin sees the exact statutory year that will be stamped. */}
                {(() => {
                  const ym = periodYearMonth
                    ? ymFromString(periodYearMonth)
                    : ymFromString(periodForm.start_date);
                  const { fy, ay } = deriveFyAy(ym.year, ym.month);
                  if (!fy) return null;
                  return (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5" data-testid="fy-ay-preview">
                      <p className="text-[11px] font-semibold text-emerald-900 mb-0.5">
                        Statutory Year Mapping
                      </p>
                      <p className="text-xs text-emerald-800">
                        This period will be stamped as
                        &nbsp;<b>FY {fy}</b>
                        &nbsp;/&nbsp;
                        <b>AY {ay}</b>
                        <span className="text-emerald-700">
                          &nbsp;— used automatically for Tax Slab lookups & Form-16.
                        </span>
                      </p>
                    </div>
                  );
                })()}
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowPeriodModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="save-period-btn" type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC]">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Statutory Modal */}
        {showStatModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowStatModal(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Configure {statType.toUpperCase()}</h3>
              <form onSubmit={handleSaveStatutory} className="space-y-4">
                {statType === 'pf' && (<>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Employee Rate (%)</label><input type="number" step="0.01" value={statForm.employee_rate || 12} onChange={(e) => setStatForm({ ...statForm, employee_rate: parseFloat(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Employer Rate (%)</label><input type="number" step="0.01" value={statForm.employer_rate || 12} onChange={(e) => setStatForm({ ...statForm, employer_rate: parseFloat(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  </div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Wage Ceiling</label><input type="number" value={statForm.wage_ceiling || 15000} onChange={(e) => setStatForm({ ...statForm, wage_ceiling: parseFloat(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                </>)}
                {statType === 'esi' && (<>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Employee Rate (%)</label><input type="number" step="0.01" value={statForm.employee_rate || 0.75} onChange={(e) => setStatForm({ ...statForm, employee_rate: parseFloat(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Employer Rate (%)</label><input type="number" step="0.01" value={statForm.employer_rate || 3.25} onChange={(e) => setStatForm({ ...statForm, employer_rate: parseFloat(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  </div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Wage Ceiling</label><input type="number" value={statForm.wage_ceiling || 21000} onChange={(e) => setStatForm({ ...statForm, wage_ceiling: parseFloat(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                </>)}
                {statType === 'pt' && (
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">State</label><input value={statForm.state || ''} onChange={(e) => setStatForm({ ...statForm, state: e.target.value })} placeholder="e.g. Maharashtra" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                )}
                {statType === 'lwf' && (<>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">State</label><input value={statForm.state || ''} onChange={(e) => setStatForm({ ...statForm, state: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Employee Amount</label><input type="number" value={statForm.employee_amount || 0} onChange={(e) => setStatForm({ ...statForm, employee_amount: parseFloat(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Employer Amount</label><input type="number" value={statForm.employer_amount || 0} onChange={(e) => setStatForm({ ...statForm, employer_amount: parseFloat(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  </div>
                </>)}
                <div><label className="flex items-center space-x-2"><input type="checkbox" checked={statForm.is_active !== false} onChange={(e) => setStatForm({ ...statForm, is_active: e.target.checked })} /><span className="text-sm text-slate-700">Active</span></label></div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowStatModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="save-statutory-btn" type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC]">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default PayrollSetup;
